import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Typography,
  Divider,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme,
  IconButton,
  Paper,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { doc, updateDoc, collection, getDocs, query, where, serverTimestamp, GeoPoint } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebaseConfig';
import { CURRENCY_OPTIONS, getCurrencySymbol } from '../currencyOptions';
import { validateLotForm } from '../lotValidation';

interface CurrencyPricingData {
  rate?: number | null;
  cap?: number | null;  // Legacy — read for backward compat, not written
  // Legacy fields for backward compatibility
  ratePerHour?: number | null;
  dailyMaximum?: number | null;
  flatFee?: number | null;
  flatFeeDuration?: number | null;
}

interface ParkingLot {
  id: string;
  lotCode?: string;
  lotName: string;
  address: string;
  latlon?: any;
  timezone?: string;
  vendorId?: string;

  // Pricing
  ratePeriod?: string;  // hour, day, week, or month
  currencies?: Record<string, CurrencyPricingData>;
  maxOvertimeHours?: number | null;
  gracePeriodMinutes?: number | null;
  // Legacy fields (for backward compatibility)
  pricingType?: string;
  ratePerHour?: number;
  dailyMaximum?: number;
  flatFee?: number;
  flatFeeDuration?: number;
  salesTaxRate?: number;

  // Space configuration
  hasAssignedSpaces?: boolean;
  totalSpots?: number;
  availability?: number;

  // Demo lots are hidden from the mobile app's location list.
  isDemo?: boolean;

  // Approval state. Used by the resubmit-on-save flow when a partner
  // edits a rejected lot.
  status?: 'pending_review' | 'approved' | 'rejected';
  rejectionReason?: string;
}

interface EditParkingLotDialogProps {
  open: boolean;
  onClose: () => void;
  lot: ParkingLot | null;
  onSuccess: () => void;
  vendorId?: string; // When set, hides vendor selector (vendor view)
  // When true, exposes admin-only fields: payment gateway (NMI) keys,
  // account status, and fees. These write to the lot's vendor doc, not
  // the lot doc itself — NMI is per-merchant-account, shared across all
  // of a partner's lots.
  adminMode?: boolean;
  // Destructive actions are kept at the bottom of the dialog (rather
  // than in the parent's action cell) so they can't be misclicked from
  // a row hover. Only rendered when the parent provides the callback;
  // partner-mounted dialogs intentionally omit these.
  onDelete?: () => void;
  onSendBackToPending?: () => void;
}

interface CurrencyPricing {
  rate: string;  // Rate per period (hour/day/week/month)
}

export default function EditParkingLotDialog({ open, onClose, lot, onSuccess, vendorId: fixedVendorId, adminMode = false, onDelete, onSendBackToPending }: EditParkingLotDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState({
    lotName: '',
    address: '',
    latitude: '',
    longitude: '',
    timezone: 'America/New_York',
    vendorId: '',
    ratePeriod: 'hour',  // hour, day, week, or month
    salesTaxRate: '',
    maxOvertimeHours: '',
    gracePeriodMinutes: '',
    hasAssignedSpaces: true,
    totalSpots: ''
  });

  // Admin-only. Demo lots are hidden from the mobile app's location list.
  const [isDemo, setIsDemo] = useState(false);

  // Admin-only: vendor doc loaded so admin can set NMI keys / status /
  // fees from the same dialog used to review the lot. vendorDocId is
  // stashed because setVendorNmiKey requires it.
  const [vendorDocId, setVendorDocId] = useState<string | null>(null);
  const [vendorCompanyName, setVendorCompanyName] = useState<string>('');
  const [vendorNmiKeyLast4, setVendorNmiKeyLast4] = useState<string>('');
  const [nmiFormData, setNmiFormData] = useState({
    nmiSecurityKey: '',   // never pre-filled; admin enters to set/change
    nmiTokenizationKey: '',
    nmiAccountStatus: 'not_connected',
    nmiOnboardingStatus: 'not_started',
    nmiPercentageFee: '',
    nmiFixedFee: '',
  });

  // Multi-currency pricing state
  const [currencies, setCurrencies] = useState<Record<string, CurrencyPricing>>({
    USD: { rate: '' }
  });

  const [selectedCurrencyToAdd, setSelectedCurrencyToAdd] = useState('');

  // Get all IANA timezones supported by the browser
  const ALL_TIMEZONES: { value: string; label: string }[] = (Intl as any).supportedValuesOf('timeZone').map((tz: string) => ({
    value: tz,
    label: tz.replace(/_/g, ' ')
  }));

  const [vendors, setVendors] = useState<any[]>([]);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const vendorsSnapshot = await getDocs(collection(db, 'vendors'));
        const vendorsList = vendorsSnapshot.docs.map(doc => ({
          vendorId: doc.data().vendorId,
          companyName: doc.data().companyName
        }));
        setVendors(vendorsList);
      } catch (error) {
        console.error('Error fetching vendors:', error);
      }
    };

    // Only the admin view renders the partner selector. Partners can't read
    // the vendors collection at all, so fetching it from their dialog just
    // produced a permission error on every open.
    if (open && !fixedVendorId) {
      fetchVendors();
    }
  }, [open, fixedVendorId]);

  // Admin-only: load the lot's vendor doc so we can pre-fill the NMI
  // fields. Reset state when the dialog closes or when the lot changes
  // to a different vendor (or no vendor at all).
  useEffect(() => {
    if (!open || !adminMode || !lot?.vendorId) {
      setVendorDocId(null);
      setVendorCompanyName('');
      setVendorNmiKeyLast4('');
      setNmiFormData({
        nmiSecurityKey: '',
        nmiTokenizationKey: '',
        nmiAccountStatus: 'not_connected',
        nmiOnboardingStatus: 'not_started',
        nmiPercentageFee: '',
        nmiFixedFee: '',
      });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'vendors'), where('vendorId', '==', lot.vendorId))
        );
        if (cancelled) return;
        if (snap.empty) {
          setVendorDocId(null);
          return;
        }
        const docSnap = snap.docs[0];
        const data = docSnap.data() as any;
        setVendorDocId(docSnap.id);
        setVendorCompanyName(data.companyName || '');
        setVendorNmiKeyLast4(data.nmiSecurityKeyLast4 || '');
        setNmiFormData({
          nmiSecurityKey: '',
          nmiTokenizationKey: data.nmiTokenizationKey || '',
          nmiAccountStatus: data.nmiAccountStatus || 'not_connected',
          nmiOnboardingStatus: data.nmiOnboardingStatus || 'not_started',
          nmiPercentageFee: data.nmiPercentageFee?.toString() || '',
          nmiFixedFee: data.nmiFixedFee?.toString() || '',
        });
      } catch (err) {
        console.error('Failed to load vendor for admin NMI section:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, adminMode, lot?.vendorId]);

  useEffect(() => {
    if (lot) {
      // Convert legacy pricingType to ratePeriod
      let ratePeriod = lot.ratePeriod || 'hour';
      if (!lot.ratePeriod && lot.pricingType) {
        // Convert legacy pricingType to ratePeriod
        ratePeriod = lot.pricingType === 'FLAT_FEE' ? 'day' : 'hour';
      }

      setFormData({
        lotName: lot.lotName || '',
        address: lot.address || '',
        latitude: lot.latlon?.latitude?.toString() || '',
        longitude: lot.latlon?.longitude?.toString() || '',
        timezone: lot.timezone || 'America/New_York',
        vendorId: lot.vendorId || '',
        ratePeriod: ratePeriod,
        salesTaxRate: lot.salesTaxRate?.toString() || '',
        maxOvertimeHours: lot.maxOvertimeHours?.toString() || '',
        // Default to 10 when the lot predates the field, matching the
        // cloud function's DEFAULT_GRACE_PERIOD_MINUTES fallback.
        gracePeriodMinutes: lot.gracePeriodMinutes != null ? lot.gracePeriodMinutes.toString() : '10',
        hasAssignedSpaces: lot.hasAssignedSpaces !== undefined ? lot.hasAssignedSpaces : true,
        totalSpots: lot.totalSpots?.toString() || ''
      });
      setIsDemo(lot.isDemo === true);

      // Load currencies - handle both new format and legacy format
      if (lot.currencies && Object.keys(lot.currencies).length > 0) {
        const loadedCurrencies: Record<string, CurrencyPricing> = {};
        Object.entries(lot.currencies).forEach(([code, pricing]) => {
          // Handle new format (rate) or legacy format (ratePerHour/flatFee)
          if (pricing.rate !== undefined) {
            loadedCurrencies[code] = {
              rate: pricing.rate?.toString() || '',
            };
          } else {
            // Legacy format - convert to new format
            const legacyRate = pricing.ratePerHour || pricing.flatFee || 0;
            loadedCurrencies[code] = {
              rate: legacyRate?.toString() || '',
            };
          }
        });
        setCurrencies(loadedCurrencies);
      } else {
        // Legacy single-currency format - convert to USD
        const legacyRate = lot.ratePerHour || lot.flatFee || 0;
        setCurrencies({
          USD: {
            rate: legacyRate?.toString() || '',
          }
        });
      }
    }
  }, [lot]);

  const handleAddCurrency = () => {
    if (selectedCurrencyToAdd && !currencies[selectedCurrencyToAdd]) {
      setCurrencies({
        ...currencies,
        [selectedCurrencyToAdd]: { rate: '' }
      });
      setSelectedCurrencyToAdd('');
    }
  };

  const handleRemoveCurrency = (code: string) => {
    const { [code]: removed, ...rest } = currencies;
    setCurrencies(rest);
  };

  const handleCurrencyPricingChange = (code: string, field: keyof CurrencyPricing, value: string) => {
    setCurrencies({
      ...currencies,
      [code]: { ...currencies[code], [field]: value }
    });
  };

  const handleSubmit = async () => {
    if (!lot) return;

    const problems = validateLotForm(formData, currencies);
    if (problems.length > 0) {
      alert('Please fix the following before saving:\n\n\u2022 ' + problems.join('\n\u2022 '));
      return;
    }

    try {
      // Create GeoPoint if coordinates provided
      let location = null;
      if (formData.latitude && formData.longitude) {
        location = new GeoPoint(
          parseFloat(formData.latitude),
          parseFloat(formData.longitude)
        );
      }

      // Build currencies object for Firestore
      const currenciesData: Record<string, any> = {};
      Object.entries(currencies).forEach(([code, pricing]) => {
        currenciesData[code] = {
          rate: pricing.rate ? parseFloat(pricing.rate) : null,
        };
      });

      const updateData: any = {
        lotName: formData.lotName,
        address: formData.address,
        latlon: location,
        timezone: formData.timezone,
        vendorId: formData.vendorId || null,

        // Pricing configuration
        ratePeriod: formData.ratePeriod,  // hour, day, week, or month
        currencies: currenciesData,
        salesTaxRate: parseFloat(formData.salesTaxRate),

        // Overtime configuration
        maxOvertimeHours: formData.maxOvertimeHours ? parseFloat(formData.maxOvertimeHours) : null,

        // Grace window (minutes). Snapshotted onto new sessions; this edit
        // does not affect already-active sessions.
        gracePeriodMinutes: formData.gracePeriodMinutes ? parseInt(formData.gracePeriodMinutes, 10) : 10,

        // Space configuration
        hasAssignedSpaces: formData.hasAssignedSpaces,
        totalSpots: parseInt(formData.totalSpots)
      };

      // Only admins set the demo flag. Partner saves leave it untouched
      // (updateDoc only writes the fields it is given).
      if (adminMode) {
        updateData.isDemo = isDemo;
      }

      // Any partner edit re-enters the review queue. Two cases:
      //   - a rejected lot being fixed = resubmission
      //   - an approved (or legacy, status-less) lot being changed = a live
      //     lot whose price, capacity or tax rate just moved, which an admin
      //     needs to see before it reaches parkers again
      // Admin edits never trigger this — admin has the explicit
      // "Send back to Pending" action instead.
      const wasRejected = lot.status === 'rejected';
      const wasLive = !lot.status || lot.status === 'approved';
      const needsReReview = !adminMode && (wasRejected || wasLive);
      if (needsReReview) {
        updateData.status = 'pending_review';
        updateData.rejectionReason = null;
        updateData.rejectedAt = null;
        updateData.rejectedBy = null;
        // Clear the previous approval so the queue doesn't read as
        // "approved by X, also pending" — mirrors the admin send-back path.
        updateData.approvedAt = null;
        updateData.approvedBy = null;
        updateData.resubmittedAt = serverTimestamp();
      }

      await updateDoc(doc(db, 'parkinglots', lot.id), updateData);

      // Admin-only: persist NMI / payment-gateway changes to the vendor
      // doc that owns this lot. NMI security key takes the encrypted
      // path via setVendorNmiKey; everything else is plaintext on the
      // vendor doc.
      if (adminMode && vendorDocId) {
        await updateDoc(doc(db, 'vendors', vendorDocId), {
          nmiTokenizationKey: nmiFormData.nmiTokenizationKey.trim() || null,
          nmiAccountStatus: nmiFormData.nmiAccountStatus,
          nmiOnboardingStatus: nmiFormData.nmiOnboardingStatus,
          nmiPercentageFee: nmiFormData.nmiPercentageFee
            ? parseFloat(nmiFormData.nmiPercentageFee)
            : null,
          nmiFixedFee: nmiFormData.nmiFixedFee
            ? parseFloat(nmiFormData.nmiFixedFee)
            : null,
        });

        if (nmiFormData.nmiSecurityKey) {
          const functions = getFunctions();
          const setNmiKey = httpsCallable(functions, 'setVendorNmiKey');
          await setNmiKey({
            vendorDocId,
            nmiSecurityKey: nmiFormData.nmiSecurityKey,
          });
        }
      }

      if (needsReReview) {
        alert(
          wasLive ?
            'Your changes have been saved and the lot has been sent back for review.\n\n' +
              'It will stop accepting new parkers until an admin approves the ' +
              'update. Sessions already running are unaffected.' :
            'Your lot has been resubmitted for review.\n\n' +
              "We'll re-check the details and let you know once it's approved."
        );
      } else {
        alert('Parking lot updated successfully!');
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating parking lot:', error);
      alert('Failed to update parking lot');
    }
  };

  const availableCurrencies = CURRENCY_OPTIONS.filter(c => !currencies[c.code]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
        {!adminMode && lot?.status === 'rejected' ? 'Edit & Resubmit Lot' : 'Edit Parking Lot'}
      </DialogTitle>
      <DialogContent>
        {!adminMode && lot?.status === 'rejected' && (
          <Alert severity="warning" sx={{ mb: 2, mt: 2 }}>
            This lot was rejected{lot.rejectionReason ? `: ${lot.rejectionReason}` : '.'}
            <br />
            Saving will resubmit it for review.
          </Alert>
        )}
        {!adminMode && (!lot?.status || lot?.status === 'approved') && (
          <Alert severity="info" sx={{ mb: 2, mt: 2 }}>
            This lot is live. Saving any change sends it back for admin review,
            so it will stop accepting new parkers until the update is approved.
            Sessions already running are unaffected.
          </Alert>
        )}
        {lot?.lotCode && (
          <Alert severity="info" sx={{ mb: 2, mt: 2 }}>
            Lot Code: <strong style={{ fontSize: '1.2em', fontFamily: 'monospace' }}>{lot.lotCode}</strong>
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {/* Basic Information */}
          <Typography variant="h6">Basic Information</Typography>
          <Divider />

          <TextField
            label="Lot Name"
            value={formData.lotName}
            onChange={(e) => setFormData({ ...formData, lotName: e.target.value })}
            required
            fullWidth
          />

          <TextField
            label="Address"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            required
            fullWidth
          />

          <FormControl fullWidth required>
            <InputLabel>Timezone</InputLabel>
            <Select
              value={formData.timezone}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
              label="Timezone"
              MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
            >
              {ALL_TIMEZONES.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              label="Latitude"
              type="number"
              value={formData.latitude}
              onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
              fullWidth
              size={isMobile ? 'small' : 'medium'}
            />

            <TextField
              label="Longitude"
              type="number"
              value={formData.longitude}
              onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
              fullWidth
              size={isMobile ? 'small' : 'medium'}
            />
          </Box>

          {!fixedVendorId && (
            <FormControl fullWidth>
              <InputLabel>Partner</InputLabel>
              <Select
                value={formData.vendorId}
                onChange={(e) => setFormData({ ...formData, vendorId: e.target.value })}
                label="Partner"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {vendors.map((vendor) => (
                  <MenuItem key={vendor.vendorId} value={vendor.vendorId}>
                    {vendor.companyName} (ID: {vendor.vendorId})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Pricing Configuration */}
          <Typography variant="h6" sx={{ mt: 2 }}>Pricing Configuration</Typography>
          <Divider />

          <FormControl fullWidth required>
            <InputLabel>Rate Period</InputLabel>
            <Select
              value={formData.ratePeriod}
              onChange={(e) => setFormData({ ...formData, ratePeriod: e.target.value })}
              label="Rate Period"
            >
              <MenuItem value="hour">Per Hour</MenuItem>
              <MenuItem value="day">Per Day</MenuItem>
              <MenuItem value="week">Per Week</MenuItem>
              <MenuItem value="month">Per Month</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Sales Tax Rate"
            type="number"
            value={formData.salesTaxRate}
            onChange={(e) => setFormData({ ...formData, salesTaxRate: e.target.value })}
            required
            helperText="Decimal format (e.g., 0.08 for 8%)"
            inputProps={{ step: "0.01", min: "0", max: "1" }}
          />

          <TextField
            fullWidth
            label="Max Overtime Hours"
            type="number"
            value={formData.maxOvertimeHours}
            onChange={(e) => setFormData({ ...formData, maxOvertimeHours: e.target.value })}
            helperText="Max overtime hours to bill after session expires (leave blank for no limit)"
            inputProps={{ step: "0.5", min: "0" }}
          />

          <TextField
            fullWidth
            label="Grace Period (minutes)"
            type="number"
            value={formData.gracePeriodMinutes}
            onChange={(e) => setFormData({ ...formData, gracePeriodMinutes: e.target.value })}
            helperText="Minutes after expiry the user can still Add Time without losing the spot. Each session snapshots this at creation — changing it affects only new sessions."
            inputProps={{ step: "1", min: "0" }}
          />

          {/* Currency Pricing */}
          <Typography variant="h6" sx={{ mt: 2 }}>Currency Pricing</Typography>
          <Divider />

          <Alert severity="info">
            Add one or more currencies with their respective pricing. Users will choose their preferred currency at checkout.
          </Alert>

          {/* Add Currency */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Add Currency</InputLabel>
              <Select
                value={selectedCurrencyToAdd}
                onChange={(e) => setSelectedCurrencyToAdd(e.target.value)}
                label="Add Currency"
                size="small"
              >
                {availableCurrencies.map((currency) => (
                  <MenuItem key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name} ({currency.symbol})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddCurrency}
              disabled={!selectedCurrencyToAdd}
            >
              Add
            </Button>
          </Box>

          {/* Currency Pricing Cards */}
          {Object.entries(currencies).map(([code, pricing]) => (
            <Paper key={code} sx={{ p: 2, mt: 1 }} variant="outlined">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Chip
                  label={`${code} (${getCurrencySymbol(code)})`}
                  color="primary"
                  variant="outlined"
                />
                {Object.keys(currencies).length > 1 && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveCurrency(code)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>

              <TextField
                fullWidth
                label={`Rate per ${formData.ratePeriod} (${getCurrencySymbol(code)})`}
                type="number"
                value={pricing.rate}
                onChange={(e) => handleCurrencyPricingChange(code, 'rate', e.target.value)}
                required
                size="small"
              />
            </Paper>
          ))}

          {/* Space Configuration */}
          <Typography variant="h6" sx={{ mt: 2 }}>Space Configuration</Typography>
          <Divider />

          <FormControlLabel
            control={
              <Switch
                checked={formData.hasAssignedSpaces}
                onChange={(e) => setFormData({ ...formData, hasAssignedSpaces: e.target.checked })}
              />
            }
            label="Has Assigned Parking Spaces"
          />

          <Alert severity="info">
            {formData.hasAssignedSpaces
              ? "Users will enter specific space codes (e.g., PQ045)"
              : "General admission - users enter lot code only (e.g., PQAAA)"}
          </Alert>

          <TextField
            fullWidth
            label="Total Parking Spots"
            type="number"
            value={formData.totalSpots}
            onChange={(e) => setFormData({ ...formData, totalSpots: e.target.value })}
            required
            helperText="Total capacity of the parking lot"
          />

          {lot?.availability !== undefined && (
            <Alert severity="warning">
              Current availability: <strong>{lot.availability}</strong> spots available
            </Alert>
          )}

          {adminMode && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={isDemo}
                    onChange={(e) => setIsDemo(e.target.checked)}
                  />
                }
                label="Demo Lot"
              />
              {isDemo && (
                <Alert severity="info">
                  Demo lots are hidden from the Locations list in the mobile app.
                </Alert>
              )}
            </>
          )}

          {/* Admin-only Partner Payment Gateway section. NMI keys live on
              the vendor doc, not the lot doc, because they're per-merchant
              account and shared across all lots a partner owns. */}
          {adminMode && lot?.vendorId && vendorDocId && (
            <>
              <Typography variant="h6" sx={{ mt: 3 }}>
                Partner Payment Gateway (NMI)
              </Typography>
              <Divider />

              <Alert severity="info">
                {vendorCompanyName
                  ? `These keys are stored on ${vendorCompanyName}'s partner record and `
                  : 'These keys are stored on the partner record and '}
                apply to every lot they own. Changing them here updates the
                partner, not just this lot.
              </Alert>

              <FormControl fullWidth>
                <InputLabel>NMI Onboarding Status</InputLabel>
                <Select
                  value={nmiFormData.nmiOnboardingStatus}
                  onChange={(e) =>
                    setNmiFormData({ ...nmiFormData, nmiOnboardingStatus: e.target.value })
                  }
                  label="NMI Onboarding Status"
                >
                  <MenuItem value="not_started">Not Started</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="NMI Private Security Key"
                value={nmiFormData.nmiSecurityKey}
                onChange={(e) =>
                  setNmiFormData({ ...nmiFormData, nmiSecurityKey: e.target.value })
                }
                placeholder={vendorNmiKeyLast4 ? `••••••${vendorNmiKeyLast4}` : ''}
                helperText={
                  vendorNmiKeyLast4
                    ? `Key on file (last 4: ${vendorNmiKeyLast4}). Enter a new value to replace it.`
                    : "Server-side API security key — stored encrypted. Routes charges to partner's merchant account."
                }
              />

              <TextField
                fullWidth
                label="NMI Public Tokenization Key (Collect.js)"
                value={nmiFormData.nmiTokenizationKey}
                onChange={(e) =>
                  setNmiFormData({ ...nmiFormData, nmiTokenizationKey: e.target.value })
                }
                helperText="Client-side Collect.js key — public by design. Must come from the same NMI merchant account as the security key above."
              />

              <FormControl fullWidth>
                <InputLabel>NMI Account Status</InputLabel>
                <Select
                  value={nmiFormData.nmiAccountStatus}
                  onChange={(e) =>
                    setNmiFormData({ ...nmiFormData, nmiAccountStatus: e.target.value })
                  }
                  label="NMI Account Status"
                >
                  <MenuItem value="not_connected">Not Connected</MenuItem>
                  <MenuItem value="pending">Pending Verification</MenuItem>
                  <MenuItem value="enabled">Enabled</MenuItem>
                  <MenuItem value="disabled">Disabled</MenuItem>
                </Select>
              </FormControl>

              <Alert severity="info">
                Custom Fee Structure (optional — leave blank to use system defaults)
              </Alert>

              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  label="NMI Percentage Fee"
                  type="number"
                  value={nmiFormData.nmiPercentageFee}
                  onChange={(e) =>
                    setNmiFormData({ ...nmiFormData, nmiPercentageFee: e.target.value })
                  }
                  helperText="e.g., 0.025 for 2.5%"
                  inputProps={{ step: '0.001', min: '0', max: '1' }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="NMI Fixed Fee ($)"
                  type="number"
                  value={nmiFormData.nmiFixedFee}
                  onChange={(e) =>
                    setNmiFormData({ ...nmiFormData, nmiFixedFee: e.target.value })
                  }
                  helperText="e.g., 0.25"
                  inputProps={{ step: '0.01', min: '0' }}
                  sx={{ flex: 1 }}
                />
              </Box>
            </>
          )}

          {adminMode && lot?.vendorId && !vendorDocId && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Partner record for vendorId {lot.vendorId} could not be loaded.
              Payment gateway fields are unavailable.
            </Alert>
          )}

          {/* Destructive actions kept at the bottom, behind their own
              confirmation prompts, so they can't be misclicked while
              skimming the edit form.
              Legacy lots have no status field but are operational by
              convention — treat them the same as explicitly approved
              when deciding whether send-back is meaningful. */}
          {(() => {
            const isOperational = !lot?.status || lot.status === 'approved';
            const showSendBack = !!onSendBackToPending && isOperational;
            const showDelete = !!onDelete;
            if (!showSendBack && !showDelete) return null;
            return (
              <>
                <Typography variant="h6" sx={{ mt: 4, color: 'error.main' }}>
                  Danger Zone
                </Typography>
                <Divider sx={{ borderColor: 'error.light' }} />
                <Alert severity="warning" sx={{ mt: 1 }}>
                  These actions can disrupt active sessions or remove this lot
                  entirely. Each one will ask you to confirm.
                </Alert>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                  {showSendBack && (
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={onSendBackToPending}
                      fullWidth
                    >
                      Send Back to Pending Approval
                    </Button>
                  )}
                  {showDelete && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={onDelete}
                      fullWidth
                    >
                      Delete Lot
                    </Button>
                  )}
                </Box>
              </>
            );
          })()}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
